/* oxlint-disable max-lines -- lifecycle orchestrator, splitting would scatter tightly-coupled control flow */
import type { AgentRunnerStatus, AgentMode, TaskContext, TaskContextDTO } from "@project/shared";
import { DEFAULT_SONNET_MODEL } from "@project/shared";
import {
  AgentConnection,
  type AgentConnectionConfig,
  type IncomingMessage,
} from "../connection/agent-connection.js";
import { ModeController, type ModeTaskContext } from "./mode-controller.js";
import { Lifecycle, DEFAULT_LIFECYCLE_CONFIG, type LifecycleConfig } from "./lifecycle.js";
import { QueryBridge } from "./query-bridge.js";
import type { AgentRunnerConfig } from "../types.js";
import {
  syncWithBaseBranch,
  ensureOnTaskBranch,
  flushAllPendingWork,
  restoreWipSnapshot,
  updateRemoteToken,
} from "./git-utils.js";
import { mapChatHistory, readAgentVersion } from "./session-runner-helpers.js";
import { handlePullBranch } from "./parent-pull-handler.js";
import { ensureClaudeCredentials, removeConveyorCredentials } from "../harness/pty/credentials.js";

// ── Configuration ──────────────────────────────────────────────────────────

export interface SessionRunnerConfig {
  connection: AgentConnectionConfig;
  agentMode?: AgentMode;
  runnerMode?: "task" | "pm" | "code-review";
  isAuto?: boolean;
  workspaceDir: string;
  model?: string;
  lifecycle?: Partial<LifecycleConfig>;
}

export interface SessionRunnerCallbacks {
  onStatusChange: (status: AgentRunnerStatus) => void | Promise<void>;
  onEvent: (event: Record<string, unknown>) => void | Promise<void>;
}

/** True for the `post_to_chat` tool by either its bare name or its MCP-namespaced
 *  form (`mcp__conveyor__post_to_chat`), so detection is harness-agnostic. */
export function isPostToChatTool(name: unknown): boolean {
  return typeof name === "string" && (name === "post_to_chat" || name.endsWith("__post_to_chat"));
}

// ── SessionRunner: main lifecycle orchestrator ─────────────────────────────

export class SessionRunner {
  readonly connection: AgentConnection;
  readonly mode: ModeController;
  readonly lifecycle: Lifecycle;

  private readonly config: SessionRunnerConfig;
  private readonly callbacks: SessionRunnerCallbacks;
  private _state: AgentRunnerStatus = "connecting";
  private stopped = false;
  private interrupted = false;
  /** Defense-in-depth: set when the agent emits a "completed" event.
   *  Prevents the core loop from processing any further messages. */
  private completedThisTurn = false;
  /** Absolute deadline (ms epoch) at which the dormant idle wait must time
   *  out. Set on the FIRST entry into dormant idle for a given completion
   *  cycle and preserved across iterations so inbound critical messages
   *  cannot extend the bound past the configured `dormantTimeoutMs`. Reset
   *  to null when the agent transitions out of dormant idle (a wake actually
   *  promotes it back to a working turn). */
  private dormantDeadline: number | null = null;

  private taskContext: ModeTaskContext | null = null;
  private fullContext: TaskContext | null = null;
  private queryBridge: QueryBridge | null = null;
  private inputResolver: ((msg: IncomingMessage | null) => void) | null = null;
  private pendingMessages: IncomingMessage[] = [];
  private prNudgeCount = 0;
  /** Set when the agent posts a substantive message to the task chat (the
   *  `post_to_chat` tool) during the current turn; reset at the start of every
   *  query. Read by the stuck-detection: an auto agent that finished without a
   *  PR but DID explain itself / ask for help in chat is waiting on a human,
   *  not silently stuck, so it is left attached rather than nudged. */
  private agentSpokeThisTurn = false;
  /** Guards overlapping runs of the periodic git flush. */
  private periodicFlushInFlight = false;

  constructor(config: SessionRunnerConfig, callbacks: SessionRunnerCallbacks) {
    this.config = config;
    this.callbacks = callbacks;

    // Compose components
    this.connection = new AgentConnection(config.connection);

    const initialMode =
      config.agentMode ??
      (config.runnerMode === "pm" ? (config.isAuto ? "auto" : "discovery") : "building");
    this.mode = new ModeController(initialMode, config.runnerMode, config.isAuto);

    const lifecycleConfig = { ...DEFAULT_LIFECYCLE_CONFIG, ...config.lifecycle };
    this.lifecycle = new Lifecycle(lifecycleConfig, {
      onHeartbeat: () => this.connection.sendHeartbeat(),
      onIdleTimeout: () => {
        process.stderr.write("[conveyor-agent] Idle timeout reached, stopping agent\n");
        this.stopped = true;
        // A prefilled initial query parks inside executeQuery (no
        // inputResolver) — abort it so the wait actually ends.
        this.queryBridge?.stop();
        if (this.inputResolver) {
          const resolver = this.inputResolver;
          this.inputResolver = null;
          resolver(null);
        }
      },
      onDormantTimeout: () => {
        process.stderr.write("[conveyor-agent] Dormant idle timeout reached, shutting down\n");
        this.stopped = true;
        this.queryBridge?.stop();
        if (this.inputResolver) {
          const resolver = this.inputResolver;
          this.inputResolver = null;
          resolver(null);
        }
      },
      onTokenRefresh: () => void this.refreshGithubToken(),
      onGitFlush: () => void this.periodicGitFlush(),
    });
  }

  get state(): AgentRunnerStatus {
    return this._state;
  }

  get sessionId(): string {
    return this.connection.sessionId;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  // ── Main lifecycle ─────────────────────────────────────────────────

  /**
   * Establish the API connection, wire callbacks, and join the session room.
   * Call this before run() when you need to send events (e.g. setup/start
   * command output) before the main agent lifecycle begins.
   */
  async connect(): Promise<void> {
    await this.setState("connecting");

    // 1. Connect
    await this.connection.connect();
    await this.setState("connected");
    this.connection.sendEvent({ type: "connected", sessionId: this.sessionId });

    // 2. Wire callbacks
    this.wireConnectionCallbacks();
    this.lifecycle.startHeartbeat();
    this.lifecycle.startTokenRefresh();
    this.lifecycle.startGitFlush();

    // 2.5. Join session room and retrieve pending messages
    const { pendingMessages: serverMessages } = await this.connection.call("connectAgent", {
      sessionId: this.sessionId,
    });
    for (const msg of serverMessages) {
      if (msg.content) {
        this.pendingMessages.push({ content: msg.content, userId: msg.userId });
      }
    }

    // 2.6. Fire-and-forget: tell the API our agent version. If a newer
    // @rallycry/conveyor-agent is on NPM, the API will queue a base image
    // rebuild for the project (deduped per-project across concurrent agents).
    // We continue running with the current version regardless.
    const agentVersion = readAgentVersion();
    if (agentVersion) {
      this.connection
        .call("notifyAgentVersion", { sessionId: this.sessionId, agentVersion })
        .catch(() => {
          // Best-effort — don't block agent startup if NPM lookup or rebuild fails
        });
    }
  }

  /**
   * Run the main agent lifecycle: fetch context, resolve mode, execute, loop.
   * Requires connect() to have been called first.
   */
  // oxlint-disable-next-line max-lines-per-function, complexity -- lifecycle orchestration is inherently sequential
  async run(): Promise<void> {
    // 3. Fetch context
    await this.setState("fetching_context");
    try {
      const ctx = await this.connection.call("getTaskContext", {
        sessionId: this.sessionId,
        includeHistory: true,
      });
      this.fullContext = this.buildFullContext(ctx);
      this.taskContext = {
        status: ctx.status,
        plan: ctx.plan,
        storyPointId:
          ctx.storyPoints === null || ctx.storyPoints === undefined
            ? null
            : String(ctx.storyPoints),
        model: ctx.model,
        githubPRUrl: ctx.githubPRUrl,
        isParentTask: this.fullContext.isParentTask,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch task context";
      this.connection.sendEvent({ type: "error", message });
      await this.callbacks.onEvent({ type: "error", message });
      await this.shutdown("error");
      return;
    }

    // 3.5. Initial git setup. In claudespace, entrypoint.sh has already done
    // fetch + checkout + merge and sets CONVEYOR_GIT_READY=1 — skip in that
    // case. For GitHub Codespaces / local dev, fall through and do it here.
    if (process.env.CONVEYOR_GIT_READY !== "1") {
      if (this.fullContext?.githubBranch) {
        ensureOnTaskBranch(this.config.workspaceDir, this.fullContext.githubBranch);
      }
      if (this.fullContext?.baseBranch) {
        syncWithBaseBranch(this.config.workspaceDir, this.fullContext.baseBranch);
      }
    }

    // 3.6. Recover uncommitted work a previous pod snapshotted to the
    // conveyor-wip/<branch> ref before dying (runs for every medium — the
    // parent-HEAD guard makes a stale snapshot a no-op).
    if (this.fullContext?.githubBranch) {
      const restored = restoreWipSnapshot(this.config.workspaceDir, this.fullContext.githubBranch);
      if (restored !== "none") {
        process.stderr.write(`[conveyor-agent] WIP snapshot restore: ${restored}\n`);
      }
    }

    // 4. Reconcile mode from server context (overrides env-var defaults)
    this.mode.applyServerMode(this.fullContext?.agentMode, this.fullContext?.isAuto);
    this.mode.resolveInitialMode(this.taskContext);

    // 4.1. When canBypassPlanning triggers for an auto task in "Open", the server
    // needs to transition the task to InProgress. Fire-and-forget — the agent
    // proceeds to build regardless; the status update is best-effort.
    if (
      this.fullContext?.isAuto &&
      this.taskContext.status === "Open" &&
      this.mode.isBuildCapable
    ) {
      void this.connection.triggerIdentification().catch(() => {});
    }

    // 4.5. Create query bridge for SDK execution
    this.queryBridge = this.createQueryBridge();

    // 5. Log initialization
    this.logInitialization();

    // 6. Execute initial mode
    const staleBatch = [...this.pendingMessages];
    const didExecuteInitialQuery = await this.executeInitialMode();

    // Clear stale pending messages from connectAgent — they were already
    // included in the initial task context (chat history). Any messages
    // that arrived via live socket events DURING initial mode are not in
    // the snapshot, so they survive. Removal is by object identity because
    // executeInitialMode may itself consume/fold entries (prefill paths).
    // Only splice when the initial mode actually ran a query (auto/building/
    // review/pack/prefill) — modes like discovery skip the query, so pending
    // messages must be retained for the core loop to process.
    if (staleBatch.length > 0 && didExecuteInitialQuery) {
      for (const stale of staleBatch) {
        const idx = this.pendingMessages.indexOf(stale);
        if (idx !== -1) this.pendingMessages.splice(idx, 1);
      }
    }

    // Discovery ExitPlanMode during the (prefilled) initial query: planning is
    // done and the runner falls through to the normal idle wait below. Clear
    // the flag so a later follow-up's completion isn't mistaken for a fresh
    // discovery completion (which would drain pending messages in coreLoop).
    if (this.queryBridge?.isDiscoveryCompleted) {
      process.stderr.write(
        "[conveyor-agent] Discovery completed during initial query — entering idle\n",
      );
      this.queryBridge.isDiscoveryCompleted = false;
    }

    // 6.5. Auto-mode stuck check: if the agent finished without a PR and didn't
    // explain itself in chat, nudge it (or stay attached for a human).
    // Skip if a user message arrived during initial execution (it interrupted the query)
    if (!this.stopped && this.pendingMessages.length === 0) {
      await this.maybeHandleStuckAuto();
    }

    // 7. Core loop: idle → message → execute → idle
    if (!this.stopped) {
      process.stderr.write(
        `[conveyor-agent] Listening for messages (mode: ${this.mode.effectiveMode})\n`,
      );
    }
    while (!this.stopped) {
      if (this._state !== "idle") await this.setState("idle");
      await this.coreLoop();
    }

    // 8. Shutdown
    await this.shutdown("finished");
  }

  /** Convenience wrapper: connect() then run(). */
  async start(): Promise<void> {
    await this.connect();
    await this.run();
  }

  // ── Core loop ──────────────────────────────────────────────────────

  // oxlint-disable-next-line complexity -- dormant idle paths add branches but are tightly coupled to the loop
  private async coreLoop(): Promise<void> {
    while (!this.stopped) {
      // Defense-in-depth: if the agent already emitted a "completed" event,
      // stop the loop.  The server-side guard should prevent messages from
      // reaching us, but if one leaks through, this prevents the completion
      // cycle (agent says "done" → system message → agent says "done" → …).
      if (this.completedThisTurn) {
        const resumed = await this.handleDormantIdle();
        if (!resumed) break;
        continue;
      }
      if (this._state === "idle") {
        this.lifecycle.startIdleTimer();
        const msg = await this.waitForMessage();
        this.lifecycle.cancelIdleTimer();

        if (!msg) {
          if (this.interrupted && !this.stopped) {
            this.interrupted = false;
            continue;
          }
          break;
        }

        this.interrupted = false;
        await this.callbacks.onEvent({
          type: "user_message",
          content: msg.content,
          userId: msg.userId,
        });

        // Execute the Claude SDK query with the user's message. A hinted
        // refine message parks in the TUI input for the human instead of
        // auto-submitting.
        if (this.prefillEligible(msg)) {
          await this.runPrefilledMessage(msg, this.mode.effectiveMode);
        } else {
          await this.setState("running");
          await this.executeQuery(msg.content);
        }

        // Discovery ExitPlanMode: agent completed its plan — shut down cleanly.
        // No idle state, no message waiting, no chance of being re-woken.
        if (this.queryBridge?.isDiscoveryCompleted) {
          process.stderr.write(
            "[conveyor-agent] Discovery completed — entering dormant idle (staying connected)\n",
          );
          this.pendingMessages.length = 0;
          if (this._state !== "idle") await this.setState("idle");
          const discoveryMsg = await this.waitForMessage();
          if (!discoveryMsg) break;
          process.stderr.write(
            "[conveyor-agent] Received message while discovery-dormant, resuming\n",
          );
          this.queryBridge.isDiscoveryCompleted = false;
          this.completedThisTurn = false;
          this.pendingMessages.unshift(discoveryMsg);
          continue;
        }

        if (this.stopped) break;
        if (this.interrupted) {
          this.interrupted = false;
          continue;
        }

        // After user message execution, run the auto-mode stuck check.
        // Skip if a new message arrived during execution (it interrupted the query)
        if (!this.stopped && this.pendingMessages.length === 0) {
          await this.maybeHandleStuckAuto();
        }
        if (!this.stopped) await this.setState("idle");
      } else if (this._state === "error") {
        await this.setState("idle");
      } else {
        break;
      }
    }
  }

  /**
   * Handle dormant-after-completed idle. Returns true if a critical message
   * woke us (caller should `continue` the loop), false if we should break
   * (stop signal or dormant timeout fired).
   *
   * The absolute deadline is set on first entry per completion cycle and
   * preserved across re-entries: an inbound critical message cannot extend
   * the bound past `dormantTimeoutMs`. Only a genuine wake (clearing
   * `completedThisTurn`) resets the deadline so the next dormant entry
   * starts a fresh window.
   */
  private async handleDormantIdle(): Promise<boolean> {
    if (this.dormantDeadline === null) {
      this.dormantDeadline = Date.now() + this.lifecycle.config.dormantTimeoutMs;
      process.stderr.write(
        "[conveyor-agent] Completed — entering dormant idle (staying connected)\n",
      );
    }
    this.pendingMessages.length = 0;
    if (this._state !== "idle") await this.setState("idle");
    const remainingMs = Math.max(0, this.dormantDeadline - Date.now());
    this.lifecycle.startDormantTimer(remainingMs);
    const dormantMsg = await this.waitForMessage();
    this.lifecycle.cancelDormantTimer();
    if (!dormantMsg) return false;
    const contentPreview =
      dormantMsg.content.length > 80 ? `${dormantMsg.content.slice(0, 80)}...` : dormantMsg.content;
    process.stderr.write(
      `[conveyor-agent] Received message while dormant, resuming: ` +
        `userId=${dormantMsg.userId}, source=${dormantMsg.source || "unknown"}, ` +
        `content="${contentPreview.replace(/\n/g, "\\n")}"\n`,
    );
    this.completedThisTurn = false;
    this.dormantDeadline = null;
    this.pendingMessages.unshift(dormantMsg);
    return true;
  }

  // ── Initial mode execution ─────────────────────────────────────────

  /** Returns true if an initial query was executed, false otherwise. */
  private async executeInitialMode(): Promise<boolean> {
    if (!this.taskContext || !this.fullContext) return false;
    const effectiveMode = this.mode.effectiveMode;

    const intrinsicDelivery = this.queryBridge?.initialPromptDelivery(this.fullContext) ?? "submit";

    // A single hinted message (Refine → Discovery/Review while the pod was
    // down) parks in the TUI input instead of auto-submitting.
    const hinted = this.prepareInitialPendingMessages(intrinsicDelivery);
    if (hinted) {
      await this.runPrefilledMessage(hinted, effectiveMode);
      return true;
    }

    // Remaining pending chat messages already direct the agent (they arrived
    // before it booted) — force submit semantics so they aren't parked behind
    // an unsubmitted prefill: building/review run the initial query as before,
    // discovery falls through to the core loop which processes them.
    const delivery = this.pendingMessages.length > 0 ? "submit" : intrinsicDelivery;

    // Submit modes auto-run their initial instructions. Prefill (PTY task
    // chat, manual mode, fresh session) spawns the TUI for ALL modes —
    // including discovery/help, which never auto-run — with the instructions
    // pre-filled and unsubmitted for the human.
    const shouldRun =
      effectiveMode === "building" ||
      effectiveMode === "auto" ||
      effectiveMode === "review" ||
      delivery === "prefill";

    if (!shouldRun) {
      await this.setState("idle");
      return false;
    }

    if (delivery === "prefill") {
      // The query parks until the human submits in the TUI. Report
      // waiting_for_input (not running) and bound the wait with the idle
      // timer; the first harness event flips state to running via the
      // onStatusChange hook in createQueryBridge, cancelling the timer.
      await this.setState("waiting_for_input");
      await this.callbacks.onEvent({ type: "execute_mode", mode: effectiveMode, delivery });
      // A chat message may have raced the awaits above — let coreLoop process
      // it instead of parking it behind an unsubmitted prefill.
      if (this.pendingMessages.length > 0) {
        if (!this.stopped) await this.setState("idle");
        return false;
      }
      this.lifecycle.startIdleTimer();
      try {
        await this.executeQuery(undefined, delivery);
      } finally {
        this.lifecycle.cancelIdleTimer();
      }
    } else {
      await this.setState("running");
      await this.callbacks.onEvent({ type: "execute_mode", mode: effectiveMode });
      await this.executeQuery(undefined, delivery);
    }
    if (!this.stopped) await this.setState("idle");
    return true;
  }

  // ── Prefill-delivery helpers ───────────────────────────────────────

  /**
   * Startup-queue preamble: the card's initial message reaches a fresh pod
   * twice — inside the task context (chat history) AND as a queued pending
   * message. When the intrinsic delivery is prefill (manual-mode fresh TUI)
   * or a hinted message is queued (Refine), drop those context-duplicates so
   * they don't force submit semantics — a genuinely-new live message still
   * does. Returns the lone hinted message to park, if any.
   */
  private prepareInitialPendingMessages(
    intrinsicDelivery: "submit" | "prefill",
  ): IncomingMessage | null {
    const hasHintedPending = this.pendingMessages.some((m) => m.delivery === "prefill");
    if (this.pendingMessages.length > 0 && (intrinsicDelivery === "prefill" || hasHintedPending)) {
      this.foldContextDuplicatePendingMessages();
    }
    return this.takePrefillHintedMessage();
  }

  /**
   * Drop pending messages whose content is already present as a user message
   * in the task-context chat history (the card's initial message is delivered
   * both ways). Hinted (prefill) messages are kept — they are consumed by the
   * dedicated prefill path.
   */
  private foldContextDuplicatePendingMessages(): void {
    const userContents = new Set(
      (this.fullContext?.chatHistory ?? [])
        .filter((m) => m.role === "user" && m.content.trim())
        .map((m) => m.content.trim()),
    );
    const kept = this.pendingMessages.filter(
      (m) => m.delivery === "prefill" || !m.content.trim() || !userContents.has(m.content.trim()),
    );
    const dropped = this.pendingMessages.length - kept.length;
    if (dropped > 0) {
      this.pendingMessages.length = 0;
      this.pendingMessages.push(...kept);
      process.stderr.write(
        `[conveyor-agent] Folded ${dropped} pending message(s) already present in chat context\n`,
      );
    }
  }

  /**
   * Honor a prefill hint only when it is unambiguous: PTY harness, a manual
   * (human-facing) agent mode, no other message queued behind it, and not an
   * automated-critical source. Everything else keeps submit semantics.
   */
  private prefillEligible(msg: IncomingMessage): boolean {
    if (msg.delivery !== "prefill") return false;
    if (this.pendingMessages.length > 0) return false;
    if (this.queryBridge?.harnessKind !== "pty") return false;
    // Auto cards must never park a prefill in the TUI — the whole point of auto
    // is to submit and keep working, and a parked prefill blocks the human from
    // typing a follow-up. Submit instead.
    if (this.mode.isAuto) return false;
    // "user" is the queue-flush default source; "mode_change" is the refine
    // wake. Anything else (system/ci_failure/review_trigger/…) must submit.
    if (msg.source && msg.source !== "mode_change" && msg.source !== "user") return false;
    // Manual modes only.
    const m = this.mode.effectiveMode;
    return m === "discovery" || m === "review" || m === "help";
  }

  /** Consume the sole pending message when it should park as a prefill. */
  private takePrefillHintedMessage(): IncomingMessage | null {
    if (this.pendingMessages.length !== 1) return null;
    const [first] = this.pendingMessages;
    if (!first || first.delivery !== "prefill") return null;
    this.pendingMessages.length = 0;
    if (this.prefillEligible(first)) return first;
    this.pendingMessages.push(first);
    return null;
  }

  /**
   * Park a message in the Connected-TUI input (prefill) and wait for the human
   * to submit. Empty content prefills the mode's initial prompt instead (the
   * latest user chat message — e.g. a Refine wake on a dormant agent). The
   * caller restores the idle state afterwards.
   */
  private async runPrefilledMessage(msg: IncomingMessage, effectiveMode: AgentMode): Promise<void> {
    await this.setState("waiting_for_input");
    await this.callbacks.onEvent({
      type: "execute_mode",
      mode: effectiveMode,
      delivery: "prefill",
    });
    this.lifecycle.startIdleTimer();
    try {
      await this.executeQuery(msg.content.trim() ? msg.content : undefined, "prefill");
    } finally {
      this.lifecycle.cancelIdleTimer();
    }
  }

  // ── Message waiting ────────────────────────────────────────────────

  private waitForMessage(): Promise<IncomingMessage | null> {
    if (this.pendingMessages.length > 0) {
      return Promise.resolve(this.pendingMessages.shift() ?? null);
    }
    return new Promise<IncomingMessage | null>((resolve) => {
      this.inputResolver = resolve;
    });
  }

  /** Inject a message (from connection callback or external source) */
  injectMessage(msg: IncomingMessage): void {
    if (this.inputResolver) {
      const resolve = this.inputResolver;
      this.inputResolver = null;
      resolve(msg);
    } else {
      this.pendingMessages.push(msg);
      // Interrupt running query so the new message is processed quickly.
      // waiting_for_input covers a prefilled-but-unsubmitted TUI: the chat
      // message supersedes the prefill (teardown + respawn as follow-up).
      if (this._state === "running" || this._state === "waiting_for_input") {
        this.queryBridge?.stop();
      }
    }
  }

  // ── Query execution with abort handling ────────────────────────────

  /** Run queryBridge.execute, swallowing abort errors from stop/softStop. */
  private async executeQuery(
    followUpContent?: string,
    promptDelivery?: "submit" | "prefill",
  ): Promise<void> {
    if (!this.fullContext || !this.queryBridge) return;
    // Reset the per-turn "agent communicated in chat" signal so the
    // stuck-detection only sees post_to_chat activity from THIS turn.
    this.agentSpokeThisTurn = false;
    try {
      await this.queryBridge.execute(this.fullContext, followUpContent, promptDelivery);
    } catch (err) {
      if (this.interrupted || this.stopped) {
        process.stderr.write("[conveyor-agent] Query aborted by stop/softStop signal\n");
        return;
      }
      throw err;
    }
  }

  // ── Stop / soft-stop ───────────────────────────────────────────────

  /** Periodic best-effort WIP commit + push during normal agent execution.
   *  Covers ungraceful pod termination (OOMKilled, node crash/eviction) where
   *  the preStop hook + SIGTERM flush don't get a chance to run. No-ops on a
   *  clean tree. Guarded so two ticks can't overlap. Never throws. */
  private async periodicGitFlush(): Promise<void> {
    if (this.periodicFlushInFlight || this.stopped) return;
    this.periodicFlushInFlight = true;
    try {
      const result = await flushAllPendingWork(this.config.workspaceDir, {
        wipMessage: "WIP: periodic auto-commit",
        refreshToken: async () => {
          try {
            const res = await this.connection.call("refreshGithubToken", {
              sessionId: this.connection.sessionId,
            });
            return res.token;
          } catch {
            return undefined;
          }
        },
      });
      if (result.hadWork) {
        process.stderr.write(
          `[conveyor-agent] Periodic git flush: branchesBackedUp=${result.branchesBackedUp} worktreesSnapshotted=${result.worktreesSnapshotted}\n`,
        );
      }
    } catch {
      // best effort — next tick will retry
    } finally {
      this.periodicFlushInFlight = false;
    }
  }

  /** Best-effort WIP commit + push on shutdown so in-flight work isn't lost
   *  when a claudespace pod is killed. Must be called BEFORE stop() so the
   *  connection is still alive for token refresh. Never throws. */
  async flushGitOnShutdown(): Promise<void> {
    try {
      const result = await flushAllPendingWork(this.config.workspaceDir, {
        wipMessage: "WIP: auto-commit on conveyor-agent shutdown",
        refreshToken: async () => {
          try {
            const res = await this.connection.call("refreshGithubToken", {
              sessionId: this.connection.sessionId,
            });
            return res.token;
          } catch {
            return undefined;
          }
        },
      });
      if (result.hadWork) {
        process.stderr.write(
          `[conveyor-agent] Shutdown git flush: branchesBackedUp=${result.branchesBackedUp} worktreesSnapshotted=${result.worktreesSnapshotted}\n`,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      process.stderr.write(`[conveyor-agent] Shutdown git flush failed: ${msg}\n`);
    }
  }

  stop(): void {
    this.stopped = true;
    this.queryBridge?.stop();
    this.lifecycle.destroy();
    this.connection.disconnect();
    if (this.inputResolver) {
      const resolver = this.inputResolver;
      this.inputResolver = null;
      resolver(null);
    }
  }

  softStop(): void {
    this.interrupted = true;
    this.queryBridge?.stop();
    // Only null-resolve if no flushed messages are already pending — a message
    // arriving via injectMessage() before the soft stop signal means the agent
    // should process it instead of discarding with a null resolution.
    if (this.inputResolver && this.pendingMessages.length === 0) {
      const resolver = this.inputResolver;
      this.inputResolver = null;
      resolver(null);
    }
  }

  // ── Auto-mode stuck detection & nudge ───────────────────────────────

  private static readonly MAX_STUCK_NUDGES = 3;

  /** The prompt delivered into the live TUI when an auto agent looks stuck. It
   *  must offer BOTH escape routes so the agent never just goes idle. */
  private static readonly STUCK_PROMPT =
    "You are in Auto mode, your task is still In Progress, and there is no open pull request. " +
    "Do ONE of these right now: (1) open a pull request with the create_pull_request tool, OR " +
    "(2) call post_to_chat to explain exactly where you are stuck and what you need from a human. " +
    "Do not finish or go idle silently.";

  /**
   * An auto task is "stuck" when its last turn ended (the call sites run
   * post-turn, so the TUI is no longer actively working), it has no open PR,
   * AND the agent did not communicate in chat this turn. If the agent posted a
   * message (explained itself / asked for help) it is waiting on a human, not
   * silently stuck, so we leave it attached instead of nudging.
   */
  private isAutoStuck(): boolean {
    if (!this.mode.isAuto || this.stopped) return false;
    if (this.queryBridge?.wasRateLimited) return false;
    if (this.prNudgeCount > SessionRunner.MAX_STUCK_NUDGES) return false;
    if (this.agentSpokeThisTurn) return false;
    if (!this.taskContext) return false;
    return this.taskContext.status === "InProgress" && !this.taskContext.githubPRUrl;
  }

  /**
   * Stop driving the agent and route the core loop into dormant idle —
   * connected and attachable, but NOT running (so no tokens are spent while it
   * waits). A human reply (chat or TUI keystroke after the next wake) resumes
   * it. This replaces the old hard `stop()`: the auto agent must never kill its
   * own session, so a person can always take over.
   */
  private enterDormantForHumanTakeover(message: string): void {
    this.connection.postChatMessage(message);
    this.completedThisTurn = true;
    void this.connection.sendHeartbeat();
  }

  private async refreshTaskContext(): Promise<void> {
    try {
      const ctx = await this.connection.call("getTaskContext", {
        sessionId: this.sessionId,
        includeHistory: false,
      });
      this.taskContext = {
        status: ctx.status,
        plan: ctx.plan,
        storyPointId:
          ctx.storyPoints === null || ctx.storyPoints === undefined
            ? null
            : String(ctx.storyPoints),
        model: ctx.model,
        githubPRUrl: ctx.githubPRUrl,
      };
      // Keep fullContext in sync for PR nudge execution
      if (this.fullContext) {
        this.fullContext.status = ctx.status;
        this.fullContext.plan = ctx.plan;
        this.fullContext.githubPRUrl = ctx.githubPRUrl;
        this.fullContext.model = ctx.model;
      }
    } catch {
      // Continue with stale context
    }
  }

  private async maybeHandleStuckAuto(): Promise<void> {
    await this.refreshTaskContext();
    if (!this.isAutoStuck()) return;

    while (!this.stopped && !this.interrupted && this.isAutoStuck()) {
      this.prNudgeCount++;

      if (this.prNudgeCount > SessionRunner.MAX_STUCK_NUDGES) {
        // Out of nudges and still no PR — DON'T shut down. Stay attached and
        // ask for a human; the agent goes dormant (not running) until someone
        // replies in chat or the TUI.
        this.enterDormantForHumanTakeover(
          `Auto-mode: I couldn't open a PR or get unstuck after ${SessionRunner.MAX_STUCK_NUDGES} attempts. ` +
            "Staying connected — reply here or in the terminal and I'll keep working.",
        );
        return;
      }

      const attempt = this.prNudgeCount;
      const chatMsg =
        attempt === 1
          ? "Auto-nudge: still In Progress with no PR — prompting the agent to finish or explain where it's stuck..."
          : `Auto-nudge (attempt ${attempt}/${SessionRunner.MAX_STUCK_NUDGES}): still no PR — prompting the agent again...`;

      this.connection.postChatMessage(chatMsg);
      await this.setState("running");
      // The nudge is delivered to the live TUI via executeQuery so the agent
      // keeps working; the event lets the API/UI surface the nudge too.
      await this.callbacks.onEvent({ type: "pr_nudge", prompt: SessionRunner.STUCK_PROMPT });

      if (this.interrupted || this.stopped) return;
      await this.executeQuery(SessionRunner.STUCK_PROMPT);
      if (this.interrupted || this.stopped) return;

      await this.refreshTaskContext();

      // The agent explained itself / asked for help in chat this turn (and still
      // has no PR) → it's waiting on a human. Stop nudging and stay attached.
      if (this.agentSpokeThisTurn && !this.taskContext?.githubPRUrl) {
        this.enterDormantForHumanTakeover(
          "Auto-mode: the agent posted an update and is waiting for input. " +
            "Staying connected — reply here or in the terminal to continue.",
        );
        return;
      }
    }
  }

  // ── Context & bridge construction ────────────────────────────────

  // oxlint-disable-next-line complexity
  private buildFullContext(ctx: TaskContextDTO): TaskContext {
    const chatHistory = mapChatHistory(ctx.chatHistory);

    return {
      taskId: ctx.id,
      projectId: ctx.projectId ?? "",
      title: ctx.title,
      description: ctx.description,
      plan: ctx.plan,
      status: ctx.status,
      chatHistory,
      agentId: ctx.agentId ?? null,
      _runnerSessionId: this.sessionId,
      agentInstructions: ctx.agentInstructions ?? "",
      model: ctx.model,
      githubBranch: ctx.githubBranch ?? "",
      baseBranch: ctx.baseBranch ?? "",
      projectName: ctx.projectName ?? null,
      projectDescription: ctx.projectDescription ?? null,
      githubPRUrl: ctx.githubPRUrl,
      claudeSessionId: ctx.claudeSessionId ?? null,
      lastSeenMessageId: ctx.lastSeenMessageId ?? null,
      isParentTask: !!ctx.parentTaskId,
      storyPoints: ctx.storyPoints ?? undefined,
      projectAgents: ctx.projectAgents ?? undefined,
      projectTags: ctx.projectTags ?? undefined,
      taskTagIds: ctx.taskTagIds ?? undefined,
      projectObjectives: ctx.projectObjectives ?? undefined,
      incidents: ctx.incidents ?? undefined,
      recentRelatedTasks: ctx.recentRelatedTasks ?? undefined,
      agentSettings: ctx.agentSettings ?? null,
      agentMode: ctx.agentMode ?? undefined,
      isAuto: ctx.isAuto,
    };
  }

  private createQueryBridge(): QueryBridge {
    const runnerConfig: AgentRunnerConfig = {
      conveyorApiUrl: this.config.connection.apiUrl,
      taskToken: this.config.connection.taskToken,
      taskId: this.fullContext?.taskId ?? "",
      model: this.fullContext?.model ?? DEFAULT_SONNET_MODEL,
      instructions: this.fullContext?.agentInstructions ?? "",
      workspaceDir: this.config.workspaceDir,
      mode: this.config.runnerMode,
      isAuto: this.config.isAuto,
    };

    const bridge = new QueryBridge(
      this.connection,
      this.mode,
      runnerConfig,
      {
        onStatusChange: (status) => {
          // A prefilled initial query reports running when the human submits
          // in the TUI (first harness event) — leave waiting_for_input and
          // stop the prefill-wait idle timer so a long working turn isn't
          // killed by it.
          if (status === "running" && this._state === "waiting_for_input") {
            this._state = "running";
            this.lifecycle.cancelIdleTimer();
          }
          return this.callbacks.onStatusChange(status as AgentRunnerStatus);
        },
        onEvent: (event) => {
          // Track when the agent posts a substantive message to chat this turn
          // (the `post_to_chat` MCP tool — namespaced `mcp__conveyor__post_to_chat`
          // under the PTY harness). This is the "Chat has a recent message from
          // the Agent" signal the stuck-detection uses; the runner's own system
          // nudges go via a different path (postChatMessage) and never appear here.
          const evt = event as { type?: string; tool?: string };
          if (evt.type === "tool_use" && isPostToChatTool(evt.tool)) {
            this.agentSpokeThisTurn = true;
          }
          // Track completion for the agent-side guard — once the agent emits
          // "completed", the core loop will stop accepting further messages.
          if ((event as Record<string, unknown>).type === "completed") {
            this.completedThisTurn = true;
            // Immediately refresh the server's heartbeat timestamp so
            // lastHeartbeatAt can't drift stale while the agent sits dormant
            // waiting for a critical wake-up (e.g. review_trigger). The
            // periodic 30s timer would otherwise leave up to a ~29s gap
            // between the `completed` event and the next heartbeat.
            void this.connection.sendHeartbeat();
          }
          return this.callbacks.onEvent(event);
        },
      },
      this.config.workspaceDir,
    );

    bridge.isParentTask = this.fullContext?.isParentTask ?? false;
    bridge.onSoftStop = () => {
      process.stderr.write("[conveyor-agent] Soft stop requested (discovery ExitPlanMode)\n");
      this.softStop();
    };

    bridge.onModeTransition = (newMode: AgentMode) => {
      const oldMode = this.mode.effectiveMode;
      process.stderr.write(`[conveyor-agent] Mode transition: ${oldMode} → ${newMode}\n`);
      this.connection.sendEvent({ type: "mode_transition", from: oldMode, to: newMode });
      this.mode.pendingModeRestart = true;
      this.connection.emitModeChanged(newMode);
      this.softStop();
    };

    return bridge;
  }

  // ── Private helpers ────────────────────────────────────────────────

  private wireConnectionCallbacks(): void {
    this.connection.onMessage((msg) => this.injectMessage(msg));
    this.connection.onStop(() => this.stop());
    this.connection.onSoftStop(() => this.softStop());
    // After a reconnect the API process may be a fresh instance (deploy,
    // restart) whose PTY scrollback ring is empty — force a repaint so the
    // Connected-TUI terminal re-seeds instead of staying blank/hidden until
    // the CLI happens to draw again.
    this.connection.onReconnected = () => this.queryBridge?.forceRepaint();
    this.connection.onModeChange((data) => {
      const action = this.mode.handleModeChange(data.agentMode);
      if (action.type === "start_auto") {
        this.connection.emitModeChanged(this.mode.effectiveMode);
        this.softStop();
      } else if (action.type === "restart_query") {
        // Server-initiated mode transition (e.g., CI-triggered review).
        // Clear claudeSessionId to prevent SDK resume across model changes
        // (reviewer agent may use a different model than the task agent).
        if (this.fullContext && action.newMode === "review") {
          this.fullContext.claudeSessionId = null;
        }
        // On building transition, re-fetch context to pick up the newly created
        // branch (branch creation is deferred until building mode).
        // Also refresh for "auto" post-exit (defense-in-depth if "auto" leaks through).
        if (
          this.fullContext &&
          (action.newMode === "building" ||
            (action.newMode === "auto" && this.mode.hasExitedPlanMode))
        ) {
          void this.refreshBranchForBuilding();
        }
        // Exit dormant idle so the review_trigger message that follows
        // is processed in the normal idle path (which handles interrupted
        // correctly without draining pendingMessages).
        this.completedThisTurn = false;
        this.connection.emitModeChanged(action.newMode);
        this.softStop();
      }
    });
    this.connection.onApiKeyUpdate((data) => {
      if (data.isSubscription) {
        process.env.CLAUDE_CODE_OAUTH_TOKEN = data.apiKey;
        delete process.env.ANTHROPIC_API_KEY;
        // The interactive TUI reads credentials from disk, not the env var —
        // refresh the synthesized file so the next PTY spawn (and any on-401
        // re-read by a live CLI) picks up the rotated subscription token.
        void ensureClaudeCredentials();
      } else {
        process.env.ANTHROPIC_API_KEY = data.apiKey;
        delete process.env.CLAUDE_CODE_OAUTH_TOKEN;
        // Drop our synthesized subscription credentials so a stale token
        // can't keep authenticating future TUI spawns over the API key.
        void removeConveyorCredentials();
      }
    });
    this.connection.onPullBranch(({ branch }) => {
      handlePullBranch(this.config.workspaceDir, branch);
    });
  }

  /** Proactively refresh the GitHub token before the 1-hour expiry. */
  private async refreshGithubToken(): Promise<void> {
    try {
      const res = await this.connection.call("refreshGithubToken", {
        sessionId: this.connection.sessionId,
      });
      updateRemoteToken(this.config.workspaceDir, res.token);
      process.env.GITHUB_TOKEN = res.token;
      process.env.GH_TOKEN = res.token;
      process.stderr.write("[conveyor-agent] Proactively refreshed GitHub token\n");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      process.stderr.write(`[conveyor-agent] Warning: proactive token refresh failed: ${msg}\n`);
    }
  }

  /** Re-fetch task context to pick up a newly created branch and check it out. */
  private async refreshBranchForBuilding(): Promise<void> {
    try {
      const ctx = await this.connection.call("getTaskContext", {
        sessionId: this.sessionId,
        includeHistory: false,
      });
      if (ctx?.githubBranch && this.fullContext) {
        this.fullContext.githubBranch = ctx.githubBranch;
        ensureOnTaskBranch(this.config.workspaceDir, ctx.githubBranch);
      }
    } catch {
      process.stderr.write(
        "[conveyor-agent] Warning: failed to refresh branch for building transition\n",
      );
    }
  }

  private async setState(status: AgentRunnerStatus): Promise<void> {
    this._state = status;
    await this.connection.emitStatus(status);
    await this.callbacks.onStatusChange(status);
  }

  private async shutdown(finalState: AgentRunnerStatus): Promise<void> {
    process.stderr.write(`[conveyor-agent] Shutdown: reason=${finalState}\n`);
    this.connection.sendEvent({ type: "shutdown", reason: finalState });
    this.lifecycle.destroy();
    try {
      await this.setState(finalState);
    } catch {
      // Best effort — when stop() already tore down the connection the status
      // RPC rejects. A stopped agent should still finish run() cleanly so the
      // process exits 0 instead of taking the error path.
    }
    this.connection.disconnect();
    this._finalState = finalState;
  }

  private _finalState: AgentRunnerStatus | null = null;

  /** The final status after run() completes. Use to determine exit code. */
  get finalState(): AgentRunnerStatus | null {
    return this._finalState;
  }

  private buildTaskContextSnapshot(): Record<string, unknown> {
    return {
      isParentTask: this.fullContext?.isParentTask ?? false,
      status: this.taskContext?.status,
      taskTitle: this.fullContext?.title,
      hasExistingPR: !!this.fullContext?.githubPRUrl,
      hasExistingSession: !!this.fullContext?.claudeSessionId,
      chatHistoryLength: this.fullContext?.chatHistory?.length ?? 0,
      tagIds: this.fullContext?.taskTagIds ?? [],
    };
  }

  private buildInitializationContext(): Record<string, unknown> {
    return {
      mode: this.mode.effectiveMode,
      runnerMode: this.config.runnerMode ?? "task",
      sessionId: this.sessionId,
      ...this.buildTaskContextSnapshot(),
      model: this.taskContext?.model,
      isAuto: this.config.isAuto ?? false,
      subscriptionKeyLabel: process.env.CONVEYOR_SUBSCRIPTION_KEY_LABEL ?? null,
    };
  }

  private logInitialization(): void {
    const context = this.buildInitializationContext();
    process.stderr.write(`[conveyor-agent] Initialized: ${JSON.stringify(context)}\n`);
    this.connection.sendEvent({ type: "session_manifest", ...context });
  }
}
